import { isFlagOn, SUBNAV_BREADCRUMBS_FLAG } from 'utils/featureFlags'

const toCrumbLabel = value => (typeof value === 'string' ? value.trim() : '')

const BREADCRUMB_URL_BASE = 'https://breadcrumb.local'
const TEACH_QUERY_PARAMS = ['class', 'site']

const pathSegments = url => {
  try {
    return new URL(url, BREADCRUMB_URL_BASE).pathname
      .split('/')
      .filter(Boolean)
      .map(segment => segment.toLowerCase())
  } catch {
    return []
  }
}

const toUrlString = (parsed, isAbsolute) => {
  if (isAbsolute) return parsed.toString()

  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

const replacePreviewSegment = (url, browserUrl) => {
  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(url)
    const parsed = new URL(url, BREADCRUMB_URL_BASE)
    const current = new URL(browserUrl, BREADCRUMB_URL_BASE)
    let replaced = false
    const segments = parsed.pathname.split('/').map(segment => {
      if (!replaced && segment.toLowerCase() === 'preview') {
        replaced = true
        return 'teach'
      }
      return segment
    })

    if (!replaced) return url

    parsed.pathname = segments.join('/')
    TEACH_QUERY_PARAMS.forEach(key => {
      const value = current.searchParams.get(key)
      if (value) parsed.searchParams.set(key, value)
    })

    return toUrlString(parsed, isAbsolute)
  } catch {
    return url
  }
}

/**
 * Contentful breadcrumb urls use /preview. Keep that on a preview page.
 * On a teach page, swap the preview path segment for teach and keep the
 * current page's class and site query params.
 * @param {string} url
 * @param {string} [browserUrl]
 * @returns {string}
 */
export const applyBrowserModeToBreadcrumbUrl = (url, browserUrl) => {
  if (!url) return url

  const currentUrl =
    browserUrl || (typeof window !== 'undefined' ? window.location.href : '')
  const segments = pathSegments(currentUrl)

  if (segments.includes('preview') || !segments.includes('teach')) {
    return url
  }

  return replacePreviewSegment(url, currentUrl)
}

/**
 * Maps a sanitized CMS breadcrumb entry to ordered crumb data.
 * Curriculum-service turns `routeCollection` into `route`.
 * Ancestor routes stay links. The current page is the page display title,
 * rendered without a url so it is not clickable.
 * A CMS title with no url is still treated as the current page when the
 * page display title is not available.
 * @param {Object} breadcrumb
 * @param {Array} [breadcrumb.route]
 * @param {string} [currentPageTitle]
 * @returns {Array<{displayTitle: string, url?: string}>}
 */
export const getBreadcrumbLinks = (breadcrumb, currentPageTitle) => {
  const routes = Array.isArray(breadcrumb?.route) ? breadcrumb.route : []

  const links = routes.reduce((crumbs, route) => {
    const displayTitle = toCrumbLabel(route?.displayTitle)

    if (!displayTitle) {
      return crumbs
    }

    const url = applyBrowserModeToBreadcrumbUrl(toCrumbLabel(route?.url))

    return [
      ...crumbs,
      url
        ? {
            displayTitle,
            url,
          }
        : { displayTitle },
    ]
  }, [])

  const pageTitle = toCrumbLabel(currentPageTitle)
  if (!pageTitle || links.length === 0) {
    return links
  }

  return [...links.filter(link => link.url), { displayTitle: pageTitle }]
}

/**
 * Whether the CMS breadcrumb will render, so pages with a legacy back link
 * can swap it out only when there is a breadcrumb to show instead.
 * @param {Object} breadcrumb
 * @returns {boolean}
 */
export const hasBreadcrumbLinks = breadcrumb =>
  isFlagOn([SUBNAV_BREADCRUMBS_FLAG]) &&
  getBreadcrumbLinks(breadcrumb).length > 0
